import { ROUTE_PATTERNS, workspacePath, workspaceSectionPath } from "@/constants/routes";
import type { WORKSPACE_SECTIONS } from "@/constants/routes";
import type { WorkspaceRole } from "@bizy/core";
import { cn } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { usePreferences } from "@/providers/preferences-provider";
import { useWorkspaces } from "@/features/workspaces/api";
import {
  Boxes,
  ChevronLeft,
  ClipboardList,
  LayoutDashboard,
  ScrollText,
  Settings,
  ShoppingCart,
  Users,
} from "lucide-react";
import type { ReactNode } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Menu } from "../ui/menu";
import { Breadcrumbs, type BreadcrumbItem } from "./breadcrumbs";
import { BrandMark } from "./public-navbar";
import { ThemeToggle } from "./theme-toggle";
import { WorkspaceSwitcher } from "./workspace-switcher";

export interface DashboardShellProps {
  workspaceSlug: string;
  workspaceId?: string;
  /** Kept for parity with `feature/saas`; unused while the preview build has no RBAC. */
  scopes: readonly string[];
  role?: WorkspaceRole;
  breadcrumbs: readonly BreadcrumbItem[];
  children: ReactNode;
}

interface NavItem {
  section: keyof typeof WORKSPACE_SECTIONS;
  label: string;
  icon: typeof LayoutDashboard;
  scope: string;
}

const NAV_ITEMS: readonly NavItem[] = [
  { section: "home", label: "Overview", icon: LayoutDashboard, scope: "reports:read" },
  { section: "pos", label: "POS", icon: ShoppingCart, scope: "pos:*" },
  { section: "orders", label: "Orders", icon: ClipboardList, scope: "orders:read" },
  { section: "products", label: "Products", icon: Boxes, scope: "products:read" },
  { section: "logs", label: "Logs", icon: ScrollText, scope: "logs:read" },
  { section: "users", label: "Users", icon: Users, scope: "users:read" },
  { section: "settings", label: "Settings", icon: Settings, scope: "settings:read" },
];

/**
 * Workspace chrome: collapsible sidebar (state persisted), dynamic breadcrumbs,
 * workspace switcher and account menu. Navigation entries are scope-filtered, so a
 * POS admin simply never sees the settings link.
 */
export const DashboardShell = ({
  workspaceSlug,
  workspaceId,
  scopes: _scopes,
  role,
  breadcrumbs,
  children,
}: DashboardShellProps) => {
  const { actor } = useAuth();
  const { sidebarCollapsed, toggleSidebar } = usePreferences();
  const workspacesQuery = useWorkspaces();
  const navigate = useNavigate();

  // Preview build: no RBAC — every section is reachable, so the sidebar is not
  // scope-filtered. (`hasScope`/`scopes` remain in place for `feature/saas`.)
  const visibleItems = NAV_ITEMS;

  return (
    <div className="flex min-h-screen bg-background">
      <aside
        className={cn(
          "hidden shrink-0 border-r border-border bg-sidebar transition-all lg:flex lg:flex-col",
          sidebarCollapsed ? "w-[4.5rem]" : "w-64",
        )}
      >
        <div className="flex h-16 items-center gap-2 border-b border-border px-4">
          <Link to={workspacePath(workspaceSlug)} className="flex items-center gap-2">
            <BrandMark />
            {sidebarCollapsed ? null : <span className="font-semibold">bizy</span>}
          </Link>
        </div>

        <div className="flex-1 space-y-1 overflow-y-auto p-3">
          {visibleItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.section}
                to={workspaceSectionPath(workspaceSlug, item.section)}
                end={item.section === "home"}
                className={({ isActive }) =>
                  cn(
                    "flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors",
                    isActive
                      ? "bg-sidebar-accent font-medium text-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )
                }
              >
                <Icon className="size-4 shrink-0" aria-hidden />
                {sidebarCollapsed ? null : <span className="truncate">{item.label}</span>}
              </NavLink>
            );
          })}
        </div>

        <div className="border-t border-border p-3">
          <Button variant="ghost" size="sm" className="w-full justify-start" onClick={toggleSidebar}>
            <ChevronLeft
              className={cn("size-4 transition-transform", sidebarCollapsed && "rotate-180")}
              aria-hidden
            />
            {sidebarCollapsed ? null : "Collapse"}
          </Button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between gap-4 border-b border-border bg-background/85 px-4 backdrop-blur">
          <div className="flex min-w-0 items-center gap-3">
            <div className="hidden min-w-56 lg:block">
              <WorkspaceSwitcher
                workspaces={workspacesQuery.data?.items ?? []}
                activeWorkspaceId={workspaceId}
              />
            </div>
            <Breadcrumbs items={breadcrumbs} className="hidden md:flex" />
          </div>

          <div className="flex items-center gap-2">
            <ThemeToggle className="hidden sm:inline-flex" />
            <Badge variant="warning" className="hidden sm:inline-flex">
              preview · no auth
            </Badge>
            <Menu
              trigger={
                <span className="flex items-center gap-2 rounded-full border border-border p-0.5 pr-3 text-xs">
                  {actor?.avatar_url ? (
                    <img src={actor.avatar_url} alt="" className="size-7 rounded-full object-cover" />
                  ) : (
                    <span className="flex size-7 items-center justify-center rounded-full bg-muted text-xs">
                      {(actor?.email ?? "?").slice(0, 1).toUpperCase()}
                    </span>
                  )}
                  <span className="hidden max-w-24 truncate sm:inline">
                    {actor?.display_name ?? "Preview"}
                  </span>
                </span>
              }
              items={[
                { label: "Switch workspace", onSelect: () => navigate(ROUTE_PATTERNS.dashboard) },
                { label: `Role: ${role ?? "—"}`, onSelect: () => undefined, disabled: true },
                { label: actor?.email ?? "preview actor", onSelect: () => undefined, disabled: true },
              ]}
            />
          </div>
        </header>

        <main className="mx-auto w-full max-w-7xl flex-1 space-y-6 p-4 sm:p-6">{children}</main>

        <footer className="border-t border-border px-4 py-3 text-xs text-muted-foreground">
          bizy · workspace dashboard
        </footer>
      </div>
    </div>
  );
};
